import { Button, Popup, Tabs } from '@approvalmax/ui/src/components';
import { domain } from 'modules/data';
import { memo } from 'react';

import {
    SectionFilters,
    SectionReportTypeLineItem,
    SectionReportTypeRequest,
    SectionVisibleColumns,
} from '../ReportEditAndCreatePopupComponents';
import { Loading } from './components/Loading/Loading';
import {
    useCheckIsRequestTypeSelected,
    usePopupReportEdit,
    useReportEditPopupTabs,
    useReportEditPopupWindow,
} from './ReportEditPopup.hooks';
import { messages } from './ReportEditPopup.messages';
import { ActiveTab, ReportEditPopupProps } from './ReportEditPopup.types';

export const ReportEditPopup = memo<ReportEditPopupProps>((props) => {
    const { reportConfig } = props;

    const { isOpen, handleClose } = useReportEditPopupWindow();
    const { tabs, activeTab, handleChangeTab } = useReportEditPopupTabs(reportConfig);

    const { isLoading, handleSave, handleChangeReportConfig } = usePopupReportEdit(reportConfig);
    const isReportTypeSelected = useCheckIsRequestTypeSelected(reportConfig);

    if (!reportConfig) {
        return null;
    }

    const title =
        reportConfig.reportType === domain.ReportType.LineItem ? messages.titleLineItem : messages.titleRequest;

    return (
        <Popup size='medium' preventAutoClose open={isOpen} onToggle={handleClose}>
            <Popup.Header
                title={title}
                progress={isLoading}
                actions={
                    <Button
                        size='medium'
                        color='blue80'
                        disabled={isLoading || !isReportTypeSelected}
                        onClick={handleSave}
                        data-qa='save-changes-button'
                    >
                        {messages.buttonSave}
                    </Button>
                }
            />

            <Popup.Body>
                {isLoading && <Loading />}

                {!isLoading && (
                    <Tabs items={tabs} value={activeTab} onChange={handleChangeTab} spacing='16 0'>
                        <Tabs.Content value={ActiveTab.ReportType}>
                            {reportConfig.reportType === domain.ReportType.LineItem ? (
                                <SectionReportTypeLineItem
                                    reportConfig={reportConfig}
                                    onReportConfigChange={handleChangeReportConfig}
                                />
                            ) : (
                                <SectionReportTypeRequest
                                    reportConfig={reportConfig}
                                    onReportConfigChange={handleChangeReportConfig}
                                />
                            )}
                        </Tabs.Content>

                        <Tabs.Content value={ActiveTab.VisibleColumns}>
                            <SectionVisibleColumns
                                reportConfig={reportConfig}
                                onReportConfigChange={handleChangeReportConfig}
                            />
                        </Tabs.Content>

                        <Tabs.Content value={ActiveTab.Filters}>
                            <SectionFilters
                                reportConfig={reportConfig}
                                onReportConfigChange={handleChangeReportConfig}
                            />
                        </Tabs.Content>
                    </Tabs>
                )}
            </Popup.Body>
        </Popup>
    );
});

ReportEditPopup.displayName = 'ReportEditPopup';
